import { type Manifest } from '@doxyme/extensions-graphql-types';
import { Specs } from '@doxyme/extensions-common';
import { _getLogger } from '@doxyme/analytics';
import { isProduction } from '../../../shared/isProduction';
import { addPropertyToWindow } from '../../../shared/addPropertyToWindow';
import { store } from '../../../stores/redux/store';
import { syncSideloadedExtensionsToMembers } from './syncSideloadedExtensionsToMembers';
import { processSideloadExtension } from './processSideloadExtension';
import { setSideloadBaseUrl } from './sideloadBaseUrlRegistry';

const logger = _getLogger('sideloadExtension', {
  component: 'extensions',
});

/**
 * Sideloads an extension into the bridge.
 *
 * This function is exposed on `window.sideloadExtension` in non-production environments
 * to allow developers and QA engineers to test extensions without registering them.
 *
 * @param baseUrl - The base URL where the extension is hosted (e.g., 'https://my-app.doxyme-ephemeral.app/branch-123/')
 * @param manifest - The extension manifest object
 *
 * @example
 * ```js
 * // In browser console (non-production only):
 * window.sideloadExtension('https://my-app.doxyme-ephemeral.app/branch/', {
 *   id: 'my-extension',
 *   name: '@doxyme/my-extension',
 *   capabilities: [{ type: 'receive-context', context: ['userId'] }]
 * });
 * ```
 */
export function sideloadExtension(baseUrl: string, manifest: Manifest): void {
  if (isProduction()) {
    logger.warn('[sideloadExtension] Sideloading is disabled on production');
    return;
  }

  try {
    new URL(baseUrl);
  } catch {
    logger.error('[sideloadExtension] Invalid baseUrl provided', { baseUrl });
    throw new Error('sideloadExtension: baseUrl must be a valid URL');
  }

  const validation = Specs.isValidManifest(manifest);
  if (!validation.isValid) {
    logger.error('[sideloadExtension] Manifest validation failed', {
      error: validation.error,
    });
    throw new Error(`sideloadExtension: ${validation.error}`);
  }

  try {
    const result = processSideloadExtension({ baseUrl, manifest });
    setSideloadBaseUrl(manifest.name, baseUrl);
    void store.dispatch(syncSideloadedExtensionsToMembers());
    logger.info('[sideloadExtension] Successfully sideloaded extension', {
      id: result.id,
      name: result.name,
      baseUrl,
    });
  } catch (error) {
    logger.error('[sideloadExtension] Failed to sideload extension', {
      error,
      manifestName: manifest.name,
      baseUrl,
    });
    throw error;
  }
}

/**
 * Exposes the sideloadExtension function on the window object in non-production environments.
 */
export const addSideloadExtensionToWindow = (): void => {
  if (!isProduction()) {
    addPropertyToWindow<typeof sideloadExtension>(
      'sideloadExtension',
      sideloadExtension,
    );
  }
};
